import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend, addMonths, subMonths, isSameDay, isAfter, startOfDay, startOfYear, endOfYear } from 'date-fns';
import {
  Plus,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronDown,
  Palmtree,
  Thermometer,
  User,
  HelpCircle,
  X,
  CalendarDays,
  Send,
  FileText,
  ChevronUp
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const TimeOff = () => {
  const { api } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  
  // Data state
  const [balances, setBalances] = useState([]);
  const [requests, setRequests] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [holidays, setHolidays] = useState([]);
  
  // UI state
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelRequestId, setCancelRequestId] = useState(null);
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState(new Date());
  const [calendarData, setCalendarData] = useState({ requests: [], holidays: [] });
  const [activeTab, setActiveTab] = useState('requests');
  const [showAllHolidays, setShowAllHolidays] = useState(false);
  
  // Request form state
  const [requestForm, setRequestForm] = useState({
    leave_type: 'annual',
    start_date: '',
    end_date: '',
    reason: ''
  });
  const [calculatedDays, setCalculatedDays] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [activeLeaveTypes, setActiveLeaveTypes] = useState([]);

  // Dynamic leave type configuration - loaded from API
  const LEAVE_TYPE_ICONS = {
    annual: { icon: Palmtree, color: 'bg-blue-50 dark:bg-blue-900/30', textColor: 'text-blue-500' },
    sick: { icon: Thermometer, color: 'bg-red-50 dark:bg-red-900/30', textColor: 'text-red-500' },
    personal: { icon: User, color: 'bg-purple-50 dark:bg-purple-900/30', textColor: 'text-purple-500' },
    casual_leave: { icon: Calendar, color: 'bg-green-50 dark:bg-green-900/30', textColor: 'text-green-500' },
    maternity: { icon: User, color: 'bg-pink-50 dark:bg-pink-900/30', textColor: 'text-pink-500' },
    study_leave: { icon: Calendar, color: 'bg-amber-50 dark:bg-amber-900/30', textColor: 'text-amber-500' },
    default: { icon: HelpCircle, color: 'bg-slate-50 dark:bg-slate-7000', textColor: 'text-slate-500' }
  };

  useEffect(() => {
    fetchAllData();
  }, [api]);

  useEffect(() => {
    fetchCalendarData();
  }, [currentCalendarMonth, api]);

  useEffect(() => {
    // Calculate working days when dates change
    if (requestForm.start_date && requestForm.end_date) {
      calculateDays();
    } else {
      setCalculatedDays(0);
    }
  }, [requestForm.start_date, requestForm.end_date]);

  const fetchAllData = async () => {
    if (!api) return;
    setLoading(true);
    try {
      const [balRes, reqRes, upRes, holRes, typesRes] = await Promise.all([
        api.get('/time-off/balances'),
        api.get('/time-off/requests'),
        api.get('/time-off/upcoming'),
        api.get('/time-off/holidays'),
        api.get('/time-off/leave-types')
      ]);
      setBalances(balRes.data);
      setRequests(reqRes.data);
      setUpcoming(upRes.data);
      setHolidays(holRes.data);
      setActiveLeaveTypes(typesRes.data);
      
      // Set default leave type to first active type
      if (typesRes.data.length > 0 && !typesRes.data.find(t => t.key === requestForm.leave_type)) {
        setRequestForm(prev => ({ ...prev, leave_type: typesRes.data[0].key }));
      }
    } catch (error) {
      console.error('Failed to fetch time off data:', error);
      toast.error('Failed to load time off data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarData = async () => {
    if (!api) return;
    try {
      const year = currentCalendarMonth.getFullYear();
      const month = currentCalendarMonth.getMonth() + 1;
      const res = await api.get(`/time-off/calendar/${year}/${month}`);
      setCalendarData(res.data);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
    }
  };

  const calculateDays = async () => {
    if (!api || !requestForm.start_date || !requestForm.end_date) return;
    try {
      const res = await api.post('/time-off/calculate-days', {
        start_date: requestForm.start_date,
        end_date: requestForm.end_date
      });
      setCalculatedDays(res.data.working_days);
    } catch (error) {
      setCalculatedDays(0);
    }
  };

  const handleSubmitRequest = async () => {
    if (!requestForm.start_date || !requestForm.end_date) {
      toast.error('Please select start and end dates');
      return;
    }
    if (calculatedDays === 0) {
      toast.error('No working days in selected range');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/time-off/requests', requestForm);
      toast.success('Time off request submitted');
      setShowRequestDialog(false);
      // Reset form with first active leave type as default
      setRequestForm({ 
        leave_type: activeLeaveTypes[0]?.key || 'sick', 
        start_date: '', 
        end_date: '', 
        reason: '' 
      });
      fetchAllData();
      fetchCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!cancelRequestId) return;
    try {
      await api.post(`/time-off/requests/${cancelRequestId}/cancel`);
      toast.success('Request cancelled');
      setShowCancelDialog(false);
      setCancelRequestId(null);
      fetchAllData();
      fetchCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel request');
    }
  };

  const getBalanceIcon = (type) => {
    const config = LEAVE_TYPE_ICONS[type] || LEAVE_TYPE_ICONS.default;
    return config.icon;
  };
  
  const getBalanceIconColor = (type) => {
    const config = LEAVE_TYPE_ICONS[type] || LEAVE_TYPE_ICONS.default;
    return config.color;
  };

  const getBalanceTextColor = (type) => {
    const config = LEAVE_TYPE_ICONS[type] || LEAVE_TYPE_ICONS.default;
    return config.textColor;
  };
  
  // Get leave type name from active types or fallback to formatted key
  const getLeaveTypeName = (key) => {
    const activeType = activeLeaveTypes.find(t => t.key === key);
    if (activeType) return activeType.name;
    // Check balances for name
    const balance = balances.find(b => b.leave_type === key);
    if (balance?.leave_type_name) return balance.leave_type_name;
    // Fallback: format key as readable name
    return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const getBalanceColor = (usagePercent) => {
    if (usagePercent < 50) return 'bg-emerald-50 dark:bg-emerald-900/30';
    if (usagePercent < 80) return 'bg-amber-50 dark:bg-amber-900/30';
    return 'bg-red-50 dark:bg-red-900/30';
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Pending', class: 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300' },
      approved: { label: 'Approved', class: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300' },
      rejected: { label: 'Rejected', class: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300' },
      cancelled: { label: 'Cancelled', class: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:bg-slate-700 dark:text-slate-400' }
    };
    const { label, class: className } = config[status] || config.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${className}`} data-testid={`status-badge-${status}`}>
        {label}
      </span>
    );
  };

  const getLeaveTypeBadge = (leaveType) => {
    const iconColor = getBalanceIconColor(leaveType);
    const textColor = getBalanceTextColor(leaveType);
    const name = getLeaveTypeName(leaveType);
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${iconColor} bg-opacity-10 ${textColor}`}>
        {name}
      </span>
    );
  };

  const formatDateRange = (startDate, endDate) => {
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    if (format(start, 'yyyy-MM') === format(end, 'yyyy-MM')) {
      return `${format(start, 'MMM d')} - ${format(end, 'd, yyyy')}`;
    }
    return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
  };

  // Quick stats calculations
  const quickStats = useMemo(() => {
    const now = new Date();
    const yearStart = startOfYear(now);
    const yearEnd = endOfYear(now);
    
    // Taken this year: approved leave days with end_date in current year
    const takenThisYear = requests
      .filter(r => r.status === 'approved' && parseISO(r.end_date) <= now && parseISO(r.end_date) >= yearStart)
      .reduce((sum, r) => sum + (r.working_days || 0), 0);
    
    // Pending approval: requests with pending status
    const pendingApproval = requests.filter(r => r.status === 'pending').length;
    
    // Upcoming approved: approved leave days in the future
    const upcomingApproved = requests
      .filter(r => r.status === 'approved' && parseISO(r.start_date) > now)
      .reduce((sum, r) => sum + (r.working_days || 0), 0);
    
    return { takenThisYear, pendingApproval, upcomingApproved };
  }, [requests]);

  // Upcoming holidays (next 3)
  const upcomingHolidays = useMemo(() => {
    const now = new Date();
    return holidays
      .filter(h => parseISO(h.date) >= now)
      .sort((a, b) => parseISO(a.date) - parseISO(b.date))
      .slice(0, 3);
  }, [holidays]);

  // All holidays for current year
  const allHolidaysThisYear = useMemo(() => {
    const now = new Date();
    const yearStart = startOfYear(now);
    const yearEnd = endOfYear(now);
    return holidays
      .filter(h => {
        const date = parseISO(h.date);
        return date >= yearStart && date <= yearEnd;
      })
      .sort((a, b) => parseISO(a.date) - parseISO(b.date));
  }, [holidays]);

  // Calendar rendering
  const calendarDays = useMemo(() => {
    const start = startOfMonth(currentCalendarMonth);
    const end = endOfMonth(currentCalendarMonth);
    const days = eachDayOfInterval({ start, end });
    
    // Add padding days for the first week
    const startDay = start.getDay();
    const paddingBefore = Array(startDay).fill(null);
    
    return [...paddingBefore, ...days];
  }, [currentCalendarMonth]);

  const getDayStatus = (date) => {
    if (!date) return null;
    const dateStr = format(date, 'yyyy-MM-dd');
    
    // Check holidays
    const holiday = calendarData.holidays?.find(h => h.date === dateStr);
    if (holiday) return { type: 'holiday', data: holiday };
    
    // Check requests
    for (const req of calendarData.requests || []) {
      if (dateStr >= req.start_date && dateStr <= req.end_date) {
        return { type: 'request', data: req };
      }
    }
    
    return null;
  };

  // Sort requests by most recent first
  const sortedRequests = useMemo(() => {
    return [...requests].sort((a, b) => parseISO(b.created_at) - parseISO(a.created_at));
  }, [requests]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <TooltipProvider delayDuration={300}>
        <div className="space-y-6" data-testid="time-off-page">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
                {t('nav.timeOff')}
              </h1>
              <p className="text-slate-500 mt-1">{t('timeOff.subtitle')}</p>
            </div>
            
            <Button
              onClick={() => setShowRequestDialog(true)}
              className="btn-premium h-10"
              data-testid="request-time-off-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('timeOff.requestTimeOff')}
            </Button>
          </div>

          {/* Compact Summary Row - Balance Cards + Stats + Next Holiday */}
          <div className="premium-card p-3" data-testid="balance-cards">
            <div className="flex flex-wrap items-center gap-4 lg:gap-6">
              {/* Leave Balances - Compact inline */}
              {balances.map((balance) => {
                const Icon = getBalanceIcon(balance.leave_type);
                const iconColor = getBalanceIconColor(balance.leave_type);
                const textColor = getBalanceTextColor(balance.leave_type);
                const remaining = balance.remaining_days ?? (balance.total_days - balance.used_days);
                const usagePercent = balance.usage_percent ?? (balance.total_days > 0 ? (balance.used_days / balance.total_days) * 100 : 0);
                const typeName = balance.leave_type_name || getLeaveTypeName(balance.leave_type);
                
                return (
                  <div key={balance.id || balance.leave_type} className="flex items-center gap-2" data-testid={`balance-${balance.leave_type}`}>
                    <div className={`w-8 h-8 rounded-lg ${iconColor} bg-opacity-10 flex items-center justify-center`}>
                      <Icon className={`w-4 h-4 ${textColor}`} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">{typeName}</p>
                      <div className="flex items-baseline gap-1">
                        <span className="text-lg font-bold text-slate-900 dark:text-white">{remaining}</span>
                        <span className="text-xs text-slate-400">/ {balance.total_days}</span>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Divider */}
              <div className="h-8 w-px bg-slate-200 dark:bg-slate-700 hidden md:block" />

              {/* Quick Stats - Inline */}
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-slate-400" />
                  <span className="text-slate-500">{t('timeOff.taken')}:</span>
                  <span className="font-semibold text-slate-700 dark:text-slate-300">{quickStats.takenThisYear}d</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-amber-50 dark:bg-amber-900/30" />
                  <span className="text-slate-500">{t('timeOff.pending')}:</span>
                  <span className="font-semibold text-amber-600">{quickStats.pendingApproval}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-50 dark:bg-emerald-900/30" />
                  <span className="text-slate-500">{t('timeOff.upcoming')}:</span>
                  <span className="font-semibold text-emerald-600">{quickStats.upcomingApproved}d</span>
                </div>
              </div>

              {/* Divider */}
              <div className="h-8 w-px bg-slate-200 dark:bg-slate-700 hidden lg:block" />

              {/* Next Holiday - Compact */}
              {upcomingHolidays.length > 0 && (
                <div className="flex items-center gap-2 ml-auto">
                  <div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/30 bg-opacity-10 flex items-center justify-center">
                    <CalendarDays className="w-4 h-4 text-red-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Next Holiday</p>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate max-w-[140px]">
                      {upcomingHolidays[0].name}
                    </p>
                    <p className="text-xs text-red-500">{format(parseISO(upcomingHolidays[0].date), 'MMM d')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tabs: My Requests / Calendar */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="requests" data-testid="tab-requests">
                <FileText className="w-4 h-4 mr-2" />
                {t('timeOff.myRequests')}
              </TabsTrigger>
            </TabsList>

            {/* Tab 1: My Requests */}
            <TabsContent value="requests" className="mt-0">
              <div className="premium-card overflow-hidden" data-testid="requests-table">
                {sortedRequests.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">{t('timeOff.noRequests')}</p>
                    <Button
                      onClick={() => setShowRequestDialog(true)}
                      className="btn-premium"
                      data-testid="empty-request-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Request Time Off
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 dark:bg-slate-800/50">
                          <tr>
                            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">{t('timeOff.leaveType')}</th>
                            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">{t('timeOff.dateRange')}</th>
                            <th className="text-center text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">{t('timeOff.days')}</th>
                            <th className="text-center text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">{t('people.status')}</th>
                            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">{t('timeOff.actionedBy')}</th>
                            <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">{t('common.actions')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                          {sortedRequests.map(req => (
                            <tr key={req.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30" data-testid={`request-row-${req.id}`}>
                              <td className="px-4 py-3">
                                {getLeaveTypeBadge(req.leave_type)}
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                                {formatDateRange(req.start_date, req.end_date)}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="text-sm font-bold text-slate-900 dark:text-white">
                                  {req.working_days}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                {getStatusBadge(req.status)}
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-500">
                                {req.actioned_by_name || '-'}
                              </td>
                              <td className="px-4 py-3 text-right">
                                {req.status === 'pending' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setCancelRequestId(req.id);
                                      setShowCancelDialog(true);
                                    }}
                                    className="h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 dark:bg-red-900/30"
                                    data-testid={`cancel-request-${req.id}`}
                                  >
                                    <X className="w-3 h-3 mr-1" />
                                    Cancel
                                  </Button>
                                )}
                                {req.status === 'rejected' && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setRequestForm({
                                            leave_type: req.leave_type,
                                            start_date: '',
                                            end_date: '',
                                            reason: req.reason
                                          });
                                          setShowRequestDialog(true);
                                        }}
                                        className="h-8 text-xs"
                                        data-testid={`resubmit-${req.id}`}
                                      >
                                        Resubmit
                                      </Button>
                                    </TooltipTrigger>
                                    {req.rejection_note && (
                                      <TooltipContent className="max-w-xs">
                                        <p className="text-xs"><strong>Rejection reason:</strong> {req.rejection_note}</p>
                                      </TooltipContent>
                                    )}
                                  </Tooltip>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-800">
                      <button
                        onClick={() => setShowRequestDialog(true)}
                        className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                        data-testid="add-request-link"
                      >
                        <Plus className="w-3 h-3" />
                        Request Time Off
                      </button>
                    </div>
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>


        {/* Request Time Off Dialog */}
        <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
          <DialogContent className="sm:max-w-md rounded-2xl" data-testid="request-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl">{t('timeOff.requestTimeOff')}</DialogTitle>
              <DialogDescription>
                Select dates and submit your leave request
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Leave Type */}
              <div className="space-y-2">
                <Label className="label-premium">Leave Type</Label>
                <Select 
                  value={requestForm.leave_type} 
                  onValueChange={(value) => setRequestForm(prev => ({ ...prev, leave_type: value }))}
                >
                  <SelectTrigger className="h-11 rounded-xl" data-testid="leave-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {activeLeaveTypes.map((leaveType) => {
                      const Icon = getBalanceIcon(leaveType.key);
                      return (
                        <SelectItem key={leaveType.key} value={leaveType.key}>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            {leaveType.name}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-premium">Start Date</Label>
                  <Input
                    type="date"
                    value={requestForm.start_date}
                    onChange={(e) => setRequestForm(prev => ({ ...prev, start_date: e.target.value }))}
                    min={
                      // Sick leave allows same day, other leave types require 1 day advance notice
                      ['sick', 'sick_leave', 'emergency', 'medical'].includes(requestForm.leave_type?.toLowerCase())
                        ? format(new Date(), 'yyyy-MM-dd')
                        : format(new Date(Date.now() + 86400000), 'yyyy-MM-dd') // tomorrow
                    }
                    className="h-11 rounded-xl"
                    data-testid="start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-premium">End Date</Label>
                  <Input
                    type="date"
                    value={requestForm.end_date}
                    onChange={(e) => setRequestForm(prev => ({ ...prev, end_date: e.target.value }))}
                    min={requestForm.start_date || format(new Date(), 'yyyy-MM-dd')}
                    className="h-11 rounded-xl"
                    data-testid="end-date-input"
                  />
                </div>
              </div>
              
              {/* Advance notice hint */}
              {!['sick', 'sick_leave', 'emergency', 'medical'].includes(requestForm.leave_type?.toLowerCase()) && (
                <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Planned leave requires at least 1 day advance notice
                </p>
              )}

              {/* Calculated Days */}
              {calculatedDays > 0 && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-500/20">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-blue-700 dark:text-blue-300">Working days</span>
                    <span className="text-xl font-bold text-blue-700 dark:text-blue-300">
                      {calculatedDays} {calculatedDays === 1 ? 'day' : 'days'}
                    </span>
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Excludes weekends and public holidays
                  </p>
                </div>
              )}

              {/* Reason */}
              <div className="space-y-2">
                <Label className="label-premium">Reason (optional)</Label>
                <Textarea
                  value={requestForm.reason}
                  onChange={(e) => setRequestForm(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Add a note for your manager..."
                  className="resize-none h-20 rounded-xl"
                  data-testid="reason-input"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowRequestDialog(false)}
                className="rounded-xl"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitRequest}
                disabled={submitting || calculatedDays === 0}
                className="btn-premium"
                data-testid="submit-request-btn"
              >
                {submitting ? (
                  <>Submitting...</>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancel Confirmation Dialog */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent className="sm:max-w-sm rounded-2xl" data-testid="cancel-dialog">
            <DialogHeader>
              <DialogTitle>Cancel Request</DialogTitle>
              <DialogDescription>
                Are you sure you want to cancel this time off request?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowCancelDialog(false)}
                className="rounded-xl"
              >
                Keep Request
              </Button>
              <Button 
                variant="destructive"
                onClick={handleCancelRequest}
                className="rounded-xl"
                data-testid="confirm-cancel-btn"
              >
                Yes, Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </TooltipProvider>
    </Layout>
  );
};

export default TimeOff;
